/**
 * usePermissions hook
 * 
 * Provides permission checking functionality with caching.
 * Fetches user permissions from the API and provides helper functions
 * to check if user has specific permissions.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getMyPermissions, getRole, type UserPermissions, type RoleDetail } from '../api/rbac';
import { useWorkspace } from './useWorkspace';
import { useRoleSwitch } from './useRoleSwitch';
import { useAuthStore } from '../stores/auth';

/** Demo mode: when true, all permission checks return true so you can see full access UI. Set to false for real behaviour. */
// const DEMO_PERMISSIONS_ALL = true;

export interface UsePermissionsResult {
  /** List of permission names the user has */
  permissions: string[];
  /** User's roles at current tenant/project */
  roles: UserPermissions['roles'];
  /** Check if user has a specific permission */
  hasPermission: (permission: string) => boolean;
  /** Check if user has any of the specified permissions (OR logic) */
  hasAnyPermission: (permissions: string[]) => boolean;
  /** Check if user has all of the specified permissions (AND logic) */
  hasAllPermissions: (permissions: string[]) => boolean;
  /** Check if user is superadmin (has "*" permission) */
  isSuperadmin: boolean;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
}

/**
 * Hook to check user permissions
 * 
 * Automatically fetches permissions for current tenant/project context.
 * Caches results in React Query for performance.
 * 
 * @example
 * ```tsx
 * const { hasPermission, isLoading } = usePermissions();
 * 
 * if (isLoading) return <Loading />;
 * 
 * if (hasPermission('reports.create')) {
 *   return <CreateButton />;
 * }
 * ```
 */
export function usePermissions(): UsePermissionsResult {
  const { tenantId, projectId } = useWorkspace();
  const { mockRoleId, isMockRoleActive } = useRoleSwitch();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // Fetch user's actual permissions
  const { data: userPermissions, isLoading: isLoadingUser, error: userError } = useQuery<UserPermissions>({
    queryKey: ['permissions', 'me', tenantId, projectId],
    queryFn: () => getMyPermissions({
      tenant_id: tenantId || undefined,
      project_id: projectId || undefined,
    }),
    // Avoid /rbac/permissions/me after logout while workspace ids are still persisted
    enabled: isAuthenticated && !isMockRoleActive,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Fetch mock role permissions if mock role is active
  const { data: mockRole, isLoading: isLoadingRole, error: roleError } = useQuery<RoleDetail>({
    queryKey: ['role', mockRoleId, 'permissions'],
    queryFn: () => getRole(mockRoleId!),
    enabled: isAuthenticated && isMockRoleActive && !!mockRoleId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Transform role permissions to UserPermissions format
  const mockPermissions: UserPermissions | undefined = useMemo(() => {
    if (!mockRole || !isMockRoleActive) return undefined;
    
    return {
      user_id: 'mock-user',
      tenant_id: tenantId || undefined,
      project_id: projectId || undefined,
      permissions: mockRole.permissions.map(p => p.name),
      roles: [{
        scope: mockRole.scope,
        role_id: mockRole.id,
        role_name: mockRole.name,
        display_name: mockRole.display_name,
      }],
    };
  }, [mockRole, isMockRoleActive, tenantId, projectId]);

  // Use mock permissions if active, otherwise use user permissions
  const data = isMockRoleActive ? mockPermissions : userPermissions;
  const isLoading = isMockRoleActive ? isLoadingRole : isLoadingUser;
  const error = isMockRoleActive ? roleError : userError;

  const permissions = useMemo(() => {
    return data?.permissions || [];
  }, [data?.permissions]);

  const roles = useMemo(() => {
    return data?.roles || [];
  }, [data?.roles]);

  const isSuperadmin = useMemo(() => {
    // if (DEMO_PERMISSIONS_ALL) return true;
    return permissions.includes('*');
  }, [permissions]);

  const hasPermission = useMemo(() => {
    return (permission: string): boolean => {
      // if (DEMO_PERMISSIONS_ALL) return true;
      if (isSuperadmin) return true;
      return permissions.includes(permission);
    };
  }, [permissions, isSuperadmin]);

  const hasAnyPermission = useMemo(() => {
    return (perms: string[]): boolean => {
      // if (DEMO_PERMISSIONS_ALL) return true;
      if (isSuperadmin) return true;
      return perms.some(perm => permissions.includes(perm));
    };
  }, [permissions, isSuperadmin]);

  const hasAllPermissions = useMemo(() => {
    return (perms: string[]): boolean => {
      // if (DEMO_PERMISSIONS_ALL) return true;
      if (isSuperadmin) return true;
      return perms.every(perm => permissions.includes(perm));
    };
  }, [permissions, isSuperadmin]);

  return {
    permissions,
    roles,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isSuperadmin,
    isLoading,
    error: error as Error | null,
  };
}

